/**
 * ServiceOrderChangePhase - Troca de categoria/fase da OS (BR-OS-021).
 *
 * O pop-up (`ServiceOrderChangePhaseDialog`) replica o leiaute do
 * `TaskChangePhaseDialog` de Tarefas/Retenção/Jurídico: cabeçalho premium
 * com ícone de ramificação, bloco "Fase atual" com bolinha colorida,
 * seleção de Categoria e Nova Fase em listas suspensas, campo Observações
 * (opcional, anexado ao evento da timeline) e rodapé fixo.
 *
 * Gatilhos padronizados:
 *  - `ServiceOrderChangePhaseButton`: botão de ícone (GitBranch + tooltip
 *    "Alterar Fase") usado na listagem de OS;
 *  - Modal de detalhes da OS: botão do cabeçalho (ServiceOrderModalHeader).
 *
 * Segurança: o seletor de destino lista apenas fases com `can_change_phase`
 * no perfil do usuário (`useServiceOrderPhasePermissions`, todas as
 * categorias); o servidor revalida em `move_service_order_phase` (v1.2.0).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-09 v2.0.0 - Leiaute unificado com TaskChangePhaseDialog
 *            (PremiumDialogHeader, Observações, filtro de permissões em todas
 *            as categorias); fase da listagem volta a ser somente exibição e
 *            a troca passa ao botão de ícone com tooltip.
 *   RELEASE: 2026-10-09 v1.0.0 - Versão inicial.
 */
import { useEffect, useMemo, useState } from 'react';
import { GitBranch, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useMoveServiceOrderPhase } from '@/hooks/useServiceOrders';
import { useServiceOrderPhasePermissions } from '@/hooks/useServiceOrderPhasePermissions';
import { createLogger } from '@/lib/logger';
import { ServiceOrderPhaseProgress } from './ServiceOrderPhaseProgress';
import { useAuth } from '@/hooks/useAuth';
import { useServiceOrderDetails } from '@/hooks/useServiceOrderDetails';

/** Logger do módulo (padrão de logging condicional do projeto) */
const logger = createLogger('ServiceOrderChangePhase');

/** Limite de caracteres da observação (proteção contra payload abusivo) */
const MAX_NOTES = 1000;

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  currentCategoryId: string | null;
  currentPhaseId: string | null;
  /** Nome/cor da fase atual (bloco "Fase atual") */
  currentPhaseName?: string | null;
  currentPhaseColor?: string | null;
}

/**
 * Pop-up "Alterar Fase" da OS — mesmo leiaute do TaskChangePhaseDialog.
 * Permite trocar de categoria junto com a fase e registrar observação.
 */
export function ServiceOrderChangePhaseDialog({
  open, onOpenChange, orderId, currentCategoryId, currentPhaseId, currentPhaseName, currentPhaseColor,
}: DialogProps) {
  const move = useMoveServiceOrderPhase();
  const { user } = useAuth();
  // Cache oficial do detalhe, sem nova consulta paralela de responsabilidade.
  const { details } = useServiceOrderDetails(open ? orderId : null);
  const isResponsible = details.data?.responsible_user_id === user?.id;

  // Estado local do formulário
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [phaseId, setPhaseId] = useState<string | undefined>(undefined);
  const [notes, setNotes] = useState('');

  // Categorias ativas e fases da categoria escolhida (mesmos hooks do formulário da OS)
  const { categories, isLoading: isLoadingCategories } = useTaskCategories(true, undefined, true);
  const { phases, isLoading: isLoadingPhases } = useTaskPhases(categoryId ?? undefined, undefined, true);
  // Permissões granulares por fase da OS em TODAS as categorias (troca de categoria)
  const { canDoOnPhase } = useServiceOrderPhasePermissions();

  // Reset ao abrir: pré-seleciona a categoria atual
  useEffect(() => {
    if (open) {
      setCategoryId(currentCategoryId ?? undefined);
      setPhaseId(undefined);
      setNotes('');
    }
  }, [open, currentCategoryId]);

  // Fases disponíveis para destino (remove a fase atual e as inativas)
  const availablePhases = useMemo(
    () => (phases as any[]).filter((p) => p.id !== currentPhaseId && p.is_active !== false),
    [phases, currentPhaseId],
  );

  const canSubmit = !!phaseId && phaseId !== currentPhaseId && !move.isPending && !!details.data && canDoOnPhase(currentPhaseId, 'can_change_phase', isResponsible) && canDoOnPhase(phaseId, 'can_change_phase', isResponsible);

  /** Executa a mudança de fase (RPC) com a observação opcional */
  const handleSubmit = () => {
    if (!phaseId || !canSubmit) return;
    const trimmed = notes.trim().slice(0, MAX_NOTES);
    move.mutate(
      { id: orderId, phaseId, categoryId: categoryId ?? null, note: trimmed || null },
      {
        onSuccess: () => {
          toast.success('Fase alterada com sucesso');
          onOpenChange(false);
        },
        onError: (e: any) => {
          logger.error('Erro ao alterar fase da OS', e);
          toast.error(e?.message ?? 'Não foi possível alterar a fase');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !move.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Cabeçalho padrão do sistema (mesmo do TaskChangePhaseDialog) */}
        <PremiumDialogHeader
          icon={GitBranch}
          title="Alterar Fase"
        />

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Fase atual */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Fase atual:</Label>
            <div className="flex items-center gap-2 text-sm">
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: currentPhaseColor || 'hsl(var(--muted-foreground))' }}
              />
              <span className="font-medium">{currentPhaseName || 'Sem fase'}</span>
            </div>
          </div>

          {/* Categoria */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Categoria</Label>
            <Select
              value={categoryId}
              onValueChange={(v) => {
                setCategoryId(v);
                setPhaseId(undefined); // fase precisa ser reescolhida
              }}
              disabled={isLoadingCategories || move.isPending}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Selecione a categoria" />
              </SelectTrigger>
              <SelectContent>
                {(categories as any[]).map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: cat.color || 'hsl(var(--muted-foreground))' }}
                      />
                      <span>{cat.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Nova fase — apenas fases com can_change_phase no perfil ficam habilitadas */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Nova Fase</Label>
            <Select
              value={phaseId}
              onValueChange={setPhaseId}
              disabled={!categoryId || isLoadingPhases || move.isPending}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder={isLoadingPhases ? 'Carregando fases...' : 'Selecione a nova fase'} />
              </SelectTrigger>
              <SelectContent>
                {availablePhases.map((phase) => {
                  // Fase destino exige can_change_phase (qualquer categoria)
                  const blocked = !details.data || !canDoOnPhase(phase.id, 'can_change_phase', isResponsible);
                  return (
                    <SelectItem key={phase.id} value={phase.id} disabled={blocked}>
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: phase.color || 'hsl(var(--muted-foreground))' }}
                        />
                        <span>{phase.name}</span>
                        {blocked && (
                          <span className="text-[10px] text-muted-foreground">(sem permissão)</span>
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
                {availablePhases.length === 0 && !isLoadingPhases && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">
                    Nenhuma outra fase disponível nesta categoria
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Observações — anexadas ao evento de troca de fase na timeline */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Observações (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES))}
              maxLength={MAX_NOTES}
              rows={3}
              placeholder="Digite observações sobre a alteração da fase..."
              disabled={move.isPending}
            />
          </div>
        </div>

        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={move.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {move.isPending && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
            Alterar Fase
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CellProps {
  phaseName: string;
  phaseColor: string | null;
  phaseSortOrder: number;
  totalPhases: number;
}

/**
 * Célula da listagem: fase somente exibição (a troca é feita pelo botão de
 * ícone `ServiceOrderChangePhaseButton`, evitando cliques acidentais).
 */
export function ServiceOrderPhaseCell(props: CellProps) {
  return <ServiceOrderPhaseProgress {...props} />;
}

interface ButtonProps {
  orderId: string;
  categoryId: string | null;
  phaseId: string | null;
  phaseName: string;
  phaseColor: string | null;
}

/**
 * Botão de ícone "Alterar Fase" (padrão das listagens): quadrado, ghost,
 * ícone de ramificação e tooltip. Abre o `ServiceOrderChangePhaseDialog`.
 */
export function ServiceOrderChangePhaseButton({ orderId, categoryId, phaseId, phaseName, phaseColor }: ButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Alterar Fase"
            onClick={(e) => {
              e.stopPropagation(); // não abrir o modal de detalhes da linha
              setOpen(true);
            }}
          >
            <GitBranch className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom"><p className="text-xs">Alterar Fase</p></TooltipContent>
      </Tooltip>
      <ServiceOrderChangePhaseDialog
        open={open}
        onOpenChange={setOpen}
        orderId={orderId}
        currentCategoryId={categoryId}
        currentPhaseId={phaseId}
        currentPhaseName={phaseName}
        currentPhaseColor={phaseColor}
      />
    </>
  );
}
